import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
} from "react";
import { useFormContext } from "react-hook-form";
import type { AppointmentBookingSchema } from "@/lib/appointments/form";
import { DRAFT_STEPS, useAppointmentDraft } from "./use-appointment-draft";
import { USE_DRAFT_DATABASE } from "./use-appointment-form";

/** Trailing debounce window: collapses a burst of edits into one PUT. */
const DEBOUNCE_MS = 500;

type DraftAutosaveState = {
	/** Debounced, deduped, abortable whole-draft autosave. Safe to call often. */
	scheduleDraftSave: () => void;
};

const DraftAutosaveContext = createContext<DraftAutosaveState>({
	scheduleDraftSave: () => {},
});

/**
 * One shared autosave for the whole appointment form.
 *
 * The scheduling pickers (visit 1 + every series visit) PUT the entire draft on
 * change; firing that per keystroke/toggle is wasteful and races. This provider
 * hands them a single `scheduleDraftSave()` that:
 * - debounces bursts into one request,
 * - skips the PUT when the form is byte-identical to the last save (dirty check),
 * - aborts an in-flight save when a newer one starts (last-write-wins),
 * and — because it lives above the pickers — survives their remounts, so a
 * pending save isn't dropped when a picker re-mounts.
 */
export function DraftAutosaveProvider({ children }: { children: ReactNode }) {
	const form = useFormContext<AppointmentBookingSchema>();
	const { saveDraft } = useAppointmentDraft();
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const controllerRef = useRef<AbortController | null>(null);
	const lastSnapshotRef = useRef<string | null>(null);

	const flush = useCallback(async () => {
		if (!USE_DRAFT_DATABASE) return;

		const values = form.getValues();
		const admins = (values.additionalSettings?.admins ?? []) as Array<{
			id?: string | number;
			isPrimary?: boolean;
		}>;
		const validAdmins = admins.filter((a) => a?.id && String(a.id) !== "0");
		const primaryAdmin = validAdmins.find((a) => a.isPrimary);
		const validPrimaryAdmin =
			primaryAdmin?.id && String(primaryAdmin.id) !== "0"
				? primaryAdmin
				: validAdmins[0];
		// L1 counts as an owner. Since the multi-select was narrowed to supporting PICs,
		// emptying it is an ordinary thing to do — and bailing out here would stop the
		// draft saving with no message at all, on a form whose slots are perfectly filled.
		const picL1 = values.additionalSettings?.picL1;
		const primaryId = validPrimaryAdmin?.id
			? String(validPrimaryAdmin.id)
			: picL1
				? String(picL1)
				: undefined;
		if (!primaryId) return;

		const snapshot = JSON.stringify(values);
		if (snapshot === lastSnapshotRef.current) return; // nothing changed

		controllerRef.current?.abort();
		const controller = new AbortController();
		controllerRef.current = controller;

		const result = await saveDraft({
			adminPicId: primaryId,
			adminIds: validAdmins.map((a) => String(a.id)),
			primaryAdminId: primaryId,
			currentStep: DRAFT_STEPS[1], // appointment_scheduling
			formData: values,
			draftId: values.formOptions?.draftId
				? String(values.formOptions.draftId)
				: undefined,
			signal: controller.signal,
		});

		if (result?.aborted || !result?.success) return; // superseded or failed

		if (result.draft?.id) {
			const currentDraftId = form.getValues("formOptions.draftId");
			if (!currentDraftId || currentDraftId !== String(result.draft.id)) {
				form.setValue("formOptions.draftId", String(result.draft.id));
			}
		}
		// Snapshot exactly what was SAVED (the values captured at flush time) with
		// the possibly-new draftId patched in — NOT a fresh getValues(). Re-reading
		// here would capture edits made during the in-flight save and record them as
		// "already saved", so the follow-up flush would skip them as unchanged and
		// silently drop the newest edit.
		const savedValues = result.draft?.id
			? {
					...values,
					formOptions: {
						...values.formOptions,
						draftId: String(result.draft.id),
					},
				}
			: values;
		lastSnapshotRef.current = JSON.stringify(savedValues);
	}, [form, saveDraft]);

	const scheduleDraftSave = useCallback(() => {
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			void flush();
		}, DEBOUNCE_MS);
	}, [flush]);

	useEffect(
		() => () => {
			if (timerRef.current) clearTimeout(timerRef.current);
			controllerRef.current?.abort();
		},
		[],
	);

	return (
		<DraftAutosaveContext.Provider value={{ scheduleDraftSave }}>
			{children}
		</DraftAutosaveContext.Provider>
	);
}

export const useDraftAutosave = () => useContext(DraftAutosaveContext);
